import { Avatar, Colors, FlexBox, H5, InlineText, PlusIcon, QuoteMarkIcon } from '@helloself/hello-self-ui';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';

import { IconMessage, IconTick } from '../../../../../ui';
import { Button } from '../../../../../ui/Button/Button';
import { usePutConnectionMutation } from '../../../../connections/model/query/connection';
import { FullProfile } from '../../../model/types';
import cities from '../../edit/basic-info/location/cities.json';
import useProfileRecordEvent from '../../hooks/useProfileRecordEvent';
import Tile from '../../Tile';

export type BasicInfoTileProps = {
    profile?: FullProfile;
    isLoading: boolean;
    profilePictureUrl?: string;
    expertId: string;
    externalId?: string;
    isConnected?: boolean;
    profilePicturePreview?: string;
    isProfileNotCompleted?: boolean;
};

export function BasicInfoTile({
    expertId,
    externalId,
    profile,
    profilePictureUrl,
    profilePicturePreview,
    isLoading,
    isConnected,
    isProfileNotCompleted = false,
}: BasicInfoTileProps) {
    const [connected, setConnected] = useState(!!isConnected);
    const history = useHistory();
    const { recordProfileEvent } = useProfileRecordEvent();
    const [putConnection] = usePutConnectionMutation();
    useEffect(() => {
        setConnected(!!isConnected);
    }, [isConnected]);

    const profilePicture = profilePicturePreview || profilePictureUrl;

    const handleSendMessage = () => {
        recordProfileEvent('Clicked Message', {
            Connected: connected,
            'Clicked from Location': 'Profile Page',
            'Profile Type': profile?.userType || 'Unknown',
            'External ID': externalId || 'Unknown',
        });
        if (!profile) {
            return;
        }

        history.push(`/messages/${profile.userType}/${expertId}`);
    };

    const handleConnect = async () => {
        recordProfileEvent('Clicked Connect', {
            Category: 'Network',
            'Clicked from Location': 'Profile Page',
            'Profile Type': profile?.userType || 'Unknown',
            'External ID': externalId || 'Unknown',
        });

        try {
            await putConnection({
                followingId: expertId,
                isFavourite: false,
            }).unwrap();
        } catch (error: unknown) {
            console.error(error);
        } finally {
            setConnected(true);
        }
    };

    if (isLoading || !profile) {
        // we should never get here if profile is undefined and isLoading is false, but in case we ever do
        // use the skeleton
        return (
            <Tile>
                <FlexBox flexDirection="column" gap="16px" data-testid="BasicInfoTile">
                    {/* avatar */}
                    <Skeleton width="120px" height="120px" borderRadius="60px" />

                    <FlexBox flexDirection="column" gap="8px">
                        <FlexBox alignItems="center" flexWrap="wrap" gap="8px">
                            {/* name */}
                            <Skeleton width="180px" height="24px" />
                            {/* pronouns */}
                            <Skeleton width="76px" height="18px" />
                        </FlexBox>
                        {/* job title */}
                        <Skeleton width="160px" height="22px" />
                        {/* qualifications */}
                        <Skeleton width="140px" height="22px" />
                        {/* location */}
                        <Skeleton width="100px" height="18px" />
                    </FlexBox>
                    <FlexBox flexDirection="column" gap="8px">
                        <QuoteMarkIcon width="30px" height="100%" color="peach50" />
                        {/* headline */}
                        <Skeleton width="280px" height="24px" />
                        <Skeleton width="260px" height="24px" />
                        <Skeleton width="220px" height="24px" />
                    </FlexBox>
                </FlexBox>
            </Tile>
        );
    }

    return (
        <Tile>
            <FlexBox flexDirection="column" gap="16px" data-testid="BasicInfoTile">
                <Avatar
                    size="120px"
                    profile={{
                        firstName: profile.firstName,
                        lastName: profile.lastName,
                        profilePicture,
                    }}
                    backgroundColor={Colors.ExpertBlue300}
                    fontSize="32px"
                    textColor={Colors.Neutral500}
                />
                <FlexBox flexDirection="column" gap="8px">
                    <FlexBox alignItems="center" flexWrap="wrap" gap="8px">
                        <H5 fontSize="24px" margin="0">
                            {profile.title ? `${profile.title} ` : ''}
                            {profile.firstName} {profile.lastName}
                        </H5>

                        {profile.pronouns && (
                            <InlineText margin="0" padding="0" fontSize="14px" lineHeight="18px">
                                ({profile.pronouns})
                            </InlineText>
                        )}
                    </FlexBox>
                    {isProfileNotCompleted ? (
                        <div className="space-y-1">
                            <Skeleton width="90%" height="16px" />
                            <Skeleton width="45%" height="16px" />
                        </div>
                    ) : (
                        <>
                            {profile.jobTitle && (
                                <InlineText margin="0" fontSize="16px" fontWeight="600" lineHeight="22px">
                                    {profile.jobTitle}
                                </InlineText>
                            )}
                            {profile.professionalQualifications && (
                                <InlineText fontSize="16px" lineHeight="22px">
                                    {profile.professionalQualifications.join(', ')}
                                </InlineText>
                            )}
                            {profile.location && (
                                <InlineText fontSize="14px" lineHeight="18px">
                                    {cities.find((city) => city.value === profile.location?.toLowerCase())?.label ||
                                        null}
                                </InlineText>
                            )}
                        </>
                    )}
                </FlexBox>
                {profile.headline && (
                    <FlexBox flexDirection="column" gap="8px">
                        <QuoteMarkIcon width="30px" height="100%" color="peach50" />
                        <InlineText padding="0" fontSize="16px">
                            {profile.headline}
                        </InlineText>
                    </FlexBox>
                )}
                {expertId && (
                    <FlexBox justifyContent="flex-start" gap="8px">
                        <Button variant={connected ? 'primary' : 'secondary'} size="small" onClick={handleSendMessage}>
                            <div className="flex items-center gap-1">
                                <div className="w-4 fw-svg">
                                    <IconMessage />
                                </div>
                                <span>Message</span>
                            </div>
                        </Button>
                        <Button
                            variant={connected ? 'secondary' : 'primary'}
                            size="small"
                            onClick={handleConnect}
                            disabled={connected}
                        >
                            {connected ? (
                                <div className="flex items-center gap-1">
                                    <div className="w-3 fw-svg">
                                        <IconTick />
                                    </div>
                                    <span>Connected</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <PlusIcon height="14px" width="14px" />
                                    Connect
                                </div>
                            )}
                        </Button>
                    </FlexBox>
                )}
            </FlexBox>
        </Tile>
    );
}
